/**
 * "Needs attention": the few counts on the dashboard that ask someone to do something, each a
 * link to the page where they do it. Built from endpoints the dashboard already calls (ticket
 * statistics, adventure stats, the performance overview), so it costs no extra API surface.
 *
 * Zero counts are left out rather than shown as "0": a strip of zeros is noise, and an empty strip
 * says the same thing more clearly.
 */

import { Link as RouterLink } from 'react-router-dom'
import { Box, Chip, Paper, Skeleton, Typography } from '@mui/material'
import type { ChipProps } from '@mui/material'
import { CheckCircleOutline } from '@mui/icons-material'
import type { PerfOverview } from '@api/types/performance'
import { usePerfOverview } from '@features/performance/hooks/usePerformance'
import { useDashboardAdventureStats, useDashboardTicketStats } from '../../hooks/useDashboardRecent'
import { useIsAdmin } from '../users/useIsAdmin'

interface AttentionItem {
  key: string
  label: string
  count: number
  to: string
  color: ChipProps['color']
}

interface PerfReading {
  data?: PerfOverview
  error: unknown
  isLoading: boolean
  refetch: () => unknown
}

/**
 * The performance endpoints are Admin-only, so a moderator's strip never asks for them: the hook
 * lives in a component only an admin renders, since a hook cannot be called conditionally.
 */
export function NeedsAttention() {
  return useIsAdmin() ? <AdminNeedsAttention /> : <NeedsAttentionView />
}

function AdminNeedsAttention() {
  return <NeedsAttentionView perf={usePerfOverview(1)} />
}

function NeedsAttentionView({ perf }: { perf?: PerfReading }) {
  const tickets = useDashboardTicketStats()
  const adventures = useDashboardAdventureStats()

  const loading = tickets.isLoading || adventures.isLoading || !!perf?.isLoading
  const failed = [
    tickets.error && 'tickets',
    adventures.error && 'adventures',
    perf?.error && 'performance',
  ].filter(Boolean) as string[]

  const items: AttentionItem[] = []
  if (tickets.data) {
    items.push(
      { key: 'escalated', label: 'Escalated tickets', count: tickets.data.escalatedTickets, to: '/tickets', color: 'error' },
      { key: 'open', label: 'Open tickets', count: tickets.data.openTickets, to: '/tickets', color: 'warning' }
    )
  }
  if (adventures.data) {
    items.push({
      key: 'moderation',
      label: 'Adventures pending moderation',
      count: adventures.data.pendingModerationCount,
      to: '/adventures?moderation=0',
      color: 'warning',
    })
  }
  if (perf?.data) {
    items.push(
      { key: 'probes-down', label: 'Probes down', count: perf.data.failingProbes, to: '/performance?tab=uptime', color: 'error' },
      { key: 'probes-slow', label: 'Probes degraded', count: perf.data.degradedProbes, to: '/performance?tab=uptime', color: 'warning' }
    )
  }
  const shown = items.filter((i) => i.count > 0)

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Needs attention
      </Typography>
      {loading ? (
        <Skeleton variant="rounded" height={32} />
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {shown.map((item) => (
            <Chip
              key={item.key}
              component={RouterLink}
              to={item.to}
              clickable
              color={item.color}
              label={`${item.count.toLocaleString()} ${item.label}`}
              sx={{ height: 36, fontWeight: 600 }}
            />
          ))}
          {shown.length === 0 && failed.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
              <CheckCircleOutline fontSize="small" />
              <Typography variant="body2">Nothing waiting: no open tickets, moderation or failing probes.</Typography>
            </Box>
          )}
          {failed.length > 0 && (
            <Typography variant="caption" color="error">
              Could not load {failed.join(', ')} counts.{' '}
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (tickets.error) tickets.refetch()
                  if (adventures.error) adventures.refetch()
                  if (perf?.error) perf.refetch()
                }}
                sx={{ border: 0, background: 'none', p: 0, color: 'primary.main', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
              >
                Retry
              </Box>
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  )
}
